import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiX, FiFileText, FiDownload, FiInfo } from 'react-icons/fi';
import styles from './GerarRelatorioProducaoModal.module.css'; 
import GerarRelatorioPDF from './GerarRelatorioProducaoNe'; 
import Swal from 'sweetalert2';
import { Pie } from 'react-chartjs-2'; 
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'; 
import axios from 'axios';
import { API_BASE_URL } from '../../config';

ChartJS.register(ArcElement, Tooltip, Legend);

const GerarRelatorioProducaoModal = ({ 
    equipeId, 
    nomeEquipe, 
    veiculos, 
    onClose, 
    filtroInicio, 
    filtroFim, 
    filtroMaquinaId
}) => {
    const [loading, setLoading] = useState(false);
    const chartRef = useRef(null); 
    const [horasData, setHorasData] = useState({ trabalhadas: 0, paradas: 0 }); 

    const veiculoFiltrado = veiculos.find(v => v.id === parseInt(filtroMaquinaId))?.modelo || 'Todas as Máquinas';

    const formatDateDisplay = (dateString) => {
        try {
            if (!dateString) return '-';
            const date = new Date(dateString + 'T00:00:00'); 
            return date.toLocaleDateString('pt-BR');
        } catch (e) {
            return dateString;
        }
    }
    
    const fetchSummaryData = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/producao/equipes/${equipeId}/horas-grafico`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    dataInicio: filtroInicio,
                    dataFim: filtroFim,
                    veiculoId: filtroMaquinaId || null
                }
            });
            setHorasData(response.data);
        } catch (error) {
            console.error("Erro ao buscar dados do gráfico:", error);
        }
    }, [equipeId, filtroInicio, filtroFim, filtroMaquinaId]); 

    useEffect(() => {
        fetchSummaryData();
    }, [fetchSummaryData]);

    const handleGerarPDF = async () => {
        if (!filtroInicio || !filtroFim) {
             Swal.fire({
                title: 'Atenção',
                text: 'As datas de Início e Fim são obrigatórias para gerar o relatório.',
                icon: 'warning',
                confirmButtonColor: '#16a34a' 
             });
             return;
        }
        
        let chartImageBase64 = null;
        if (chartRef.current) {
            chartImageBase64 = chartRef.current.toBase64Image('image/png', 1.0); 
        }

        setLoading(true);
        try {
            await GerarRelatorioPDF({ 
                equipeId,
                nomeEquipe,
                filtroInicio,
                filtroFim,
                filtroMaquinaId: filtroMaquinaId !== '' ? parseInt(filtroMaquinaId) : null,
                veiculoFiltrado,
                chartImageBase64 
            });

            Swal.fire({
                title: 'Sucesso', 
                text: 'O relatório foi gerado e o download deve ter iniciado.', 
                icon: 'success',
                confirmButtonColor: '#16a34a' 
            });

        } catch (error) {
            console.error("Erro ao gerar relatório:", error);
             Swal.fire({
                title: 'Erro', 
                text: error.message || 'Houve um problema ao processar a geração do relatório.', 
                icon: 'error',
                confirmButtonColor: '#16a34a' 
             });
        } finally {
            setLoading(false);
            onClose(); 
        }
    };
    
    const pieChartData = {
        labels: ['Horas Trabalhadas', 'Horas Paradas'],
        datasets: [
            {
                label: 'Distribuição de Horas',
                data: [horasData.trabalhadas, horasData.paradas],
                backgroundColor: [
                    'rgba(22, 163, 74, 0.8)', 
                    'rgba(239, 68, 68, 0.8)', 
                ],
                borderColor: [
                    '#fff',
                    '#fff',
                ],
                borderWidth: 2,
            },
        ],
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: true, 
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    font: {
                        size: 9,
                    },
                    boxWidth: 10,
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed !== null) {
                            label += context.parsed.toFixed(2) + ' h';
                        }
                        return label;
                    }
                }
            }
        },
    };

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.header}>
                    <h2 className={styles.title}>
                        <FiFileText /> Gerar Relatório de Produção Detalhado
                    </h2>
                    <button onClick={onClose} className={styles.closeButton}>
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.infoBox}>
                    <FiInfo size={24} style={{ color: '#0ea5e9' }} />
                    <p>
                        Este relatório gerará um PDF detalhado com a produção diária, horímetros, paradas e abastecimento, utilizando os filtros ativos.
                    </p>
                </div>

                <div className={styles.contentContainer}>
                    <div className={styles.filterSummary}>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Equipe:</span>
                            <span className={styles.summaryValue}>{nomeEquipe}</span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Período:</span>
                            <span className={styles.summaryValue}>{formatDateDisplay(filtroInicio)} até {formatDateDisplay(filtroFim)}</span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryLabel}>Máquina Filtrada:</span>
                            <span className={styles.summaryValue}>{veiculoFiltrado}</span>
                        </div>
                    </div>
                    
                    {horasData && (horasData.trabalhadas > 0 || horasData.paradas > 0) ? (
                        <div className={styles.chartContainer}>
                            <Pie 
                                data={pieChartData} 
                                options={pieChartOptions} 
                                ref={chartRef} 
                            />
                        </div>
                    ) : (
                        <div className={styles.chartContainer} style={{ color: 'var(--color-text-secondary)' }}>
                           <p>Sem dados de horas para o gráfico no período selecionado.</p>
                        </div>
                    )}
                </div>

                <div className={styles.actions}>
                    <button 
                        onClick={onClose} 
                        className={styles.btnCancel}
                        disabled={loading}
                    >
                        Cancelar
                    </button>
                    <button 
                        onClick={handleGerarPDF} 
                        className={styles.btnGenerate}
                        disabled={loading || !filtroInicio || !filtroFim}
                    >
                        {loading ? 'Gerando PDF...' : <><FiDownload size={18} /> Gerar e Baixar Relatório</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GerarRelatorioProducaoModal;